import React from 'react';

import { Chip, CircularProgress } from '@mui/material';

import { type EgsRoomMatch, getRoomDataState, unhandledEgsMatch } from './egsRoomMatcher';

interface Props {
  error: unknown;
  hasRoomTypes: boolean;
  isLoading: boolean;
  match: EgsRoomMatch;
}

// Order matters: with no room types to compare against, the chip withholds a verdict rather than saying
// "No match". Nothing here promises the apply will succeed.
export default function EgsMatchStatusChip({ error, hasRoomTypes, isLoading, match }: Props) {
  const roomDataState = getRoomDataState({ error, hasRoomTypes, isLoading });

  if (roomDataState === 'error') {
    return <>—</>;
  }

  if (roomDataState === 'loading') {
    return <CircularProgress size={16} />;
  }

  // Read from the record name alone, like `all_rooms`, so it stands whether or not our room data arrived.
  if (match.source === 'rate_plan_qualified') {
    return <Chip color="warning" label="Pick rate plan" size="small" variant="outlined" />;
  }

  if (match.source === 'all_rooms') {
    return <Chip color="info" label="All rooms" size="small" variant="outlined" />;
  }

  if (roomDataState === 'unavailable') {
    return <>—</>;
  }

  const noMatch = <Chip label="No match" size="small" variant="outlined" />;

  switch (match.source) {
    case 'matched':
      return match.matchedRoomTypes.length > 1 ? (
        <Chip color="warning" label="Pick room" size="small" variant="outlined" />
      ) : (
        <Chip color="success" label="Matched" size="small" variant="outlined" />
      );

    case 'suggested':
      return <Chip color="warning" label="Pick room" size="small" variant="outlined" />;

    case 'unmatched':
      return noMatch;

    default:
      return unhandledEgsMatch(match, noMatch);
  }
}
